import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  MagnifyingGlassIcon,
  UserIcon,
  BuildingOfficeIcon,
  CheckIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const CustomerDropdown = ({ 
  orderType = 'customers',
  selectedId = null, 
  selectedName = '', 
  customers = [], 
  suppliers = [], 
  onSelect
}) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'search'
  const [isSearching, setIsSearching] = useState(false);
  
  const entities = orderType === 'customers' ? customers : suppliers;
  
  const getEntityName = (ent) => {
    if (ent.name) return ent.name;
    const first = ent.firstName || '';
    const last = ent.lastName || '';
    return `${first} ${last}`.trim();
  };
  
  const filteredEntities = searchQuery.trim() === ''
    ? []
    : entities.filter(entity => {
        const query = searchQuery.toLowerCase();
        const name = getEntityName(entity).toLowerCase();
        const id = `${entity.id ?? ''}`.toLowerCase();
        return name.includes(query) || id.includes(query);
      });
      
  const handleSelect = (entity) => {
    if (onSelect) {
      onSelect(entity);
    }
    setIsOpen(false);
    setSearchQuery('');
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    if (isOpen) {
      const closeOnOutsideClick = (e) => {
        if (!e.target.closest('.customer-dropdown-menu')) {
          setIsOpen(false);
        }
      };
      
      document.addEventListener('mousedown', closeOnOutsideClick);
      return () => document.removeEventListener('mousedown', closeOnOutsideClick);
    }
  }, [isOpen]);
  
  return (
    <div className="relative z-50">
      <div 
        className={`w-full pl-10 p-3 ${
          orderType === 'customers' && selectedName ? 
          'bg-blue-500/20 border-blue-400/30' : 
          orderType === 'suppliers' && selectedName ? 
          'bg-purple-500/20 border-purple-400/30' : 
          'bg-white/10 border-white/20'
        } border rounded-xl text-white flex justify-between items-center cursor-pointer hover:bg-white/15 transition-colors`}
        onClick={() => setIsOpen(true)}
      >
        <span>
          {selectedName || `-- Sélectionner un ${orderType === 'customers' ? 'client' : 'fournisseur'} --`}
        </span>
        {selectedName ? (
          <CheckIcon className="h-5 w-5 text-green-400" />
        ) : (
          <PlusIcon className="h-5 w-5 text-white/50" />
        )}
      </div>
      
      {isOpen && createPortal(
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
          <div className="customer-dropdown-menu bg-gray-800/95 backdrop-blur-lg rounded-xl border border-white/20 shadow-2xl overflow-hidden max-w-lg w-11/12 max-h-[90vh] z-[1001]">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-white">
                  {orderType === 'customers' ? 'Sélectionner un client' : 'Sélectionner un fournisseur'}
                </h3>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="p-1 bg-white/10 hover:bg-white/20 rounded-full"
                >
                  <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="relative mb-4">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={`Rechercher un ${orderType === 'customers' ? 'client' : 'fournisseur'}...`}
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setIsSearching(true);
                    setTimeout(() => setIsSearching(false), 300);
                    if (e.target.value.trim() !== '') {
                      setViewMode('search');
                    }
                  }}
                  className="w-full pl-10 p-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              
              <div className="flex mb-4 space-x-2">
                <button
                  type="button"
                  onClick={() => setViewMode('search')}
                  className={`flex-1 py-2 ${viewMode === 'search' 
                    ? 'bg-blue-500/50 text-white' 
                    : 'bg-white/10 hover:bg-white/20 text-white/80'} 
                    rounded-xl transition-colors text-sm font-medium`}
                >
                  Recherche
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setViewMode('list');
                    setSearchQuery('');
                  }}
                  className={`flex-1 py-2 ${viewMode === 'list' 
                    ? 'bg-blue-500/50 text-white' 
                    : 'bg-white/10 hover:bg-white/20 text-white/80'} 
                    rounded-xl transition-colors text-sm font-medium`}
                >
                  Liste complète
                </button>
              </div>
              
              {/* Entity count info */}
              <div className="text-xs text-white/50 mb-2 flex justify-between items-center">
                {orderType === 'customers' ? 
                  `${customers.length} clients disponibles` : 
                  `${suppliers.length} fournisseurs disponibles`}
                
                <span className="text-xs text-blue-400 hover:text-blue-300 cursor-pointer"
                      onClick={() => navigate(`/${orderType === 'customers' ? 'customers' : 'suppliers'}/new`)}>
                  + Créer nouveau
                </span>
              </div>
              
              <div className="mb-4">
                {viewMode === 'search' ? (
                  isSearching ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                    </div>
                  ) : searchQuery.trim() !== '' ? (
                    filteredEntities.length > 0 ? (
                      <div className="max-h-60 overflow-y-auto bg-white/5 rounded-xl">
                        {filteredEntities.map((entity) => (
                          <div
                            key={entity.id}
                            className="flex items-center justify-between p-3 hover:bg-blue-500/20 cursor-pointer transition-colors border-b border-white/5 last:border-0"
                            onClick={() => handleSelect(entity)}
                          >
                            <div>
                              <p className="text-white font-medium">{getEntityName(entity)}</p>
                              <p className="text-white/60 text-sm">
                                {entity.email && <span>{entity.email}</span>}
                                {entity.email && entity.phone && <span className="mx-1">•</span>}
                                {entity.phone && <span>{entity.phone}</span>}
                                {(entity.email || entity.phone) && entity.wilaya && <span className="mx-1">•</span>}
                                {entity.wilaya && <span>{entity.wilaya}</span>}
                              </p>
                            </div>
                            <CheckIcon className="h-5 w-5 text-blue-400" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-white/5 rounded-xl p-4 text-center">
                        <p className="text-white/60 mb-2">Aucun résultat trouvé</p>
                        <button
                          type="button"
                          onClick={() => navigate(`/${orderType === 'customers' ? 'customers' : 'suppliers'}/new`)}
                          className="text-sm px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                        >
                          Créer {orderType === 'customers' ? 'un client' : 'un fournisseur'}
                        </button>
                      </div>
                    )
                  ) : (
                    <p className="text-center py-4 text-white/60">
                      {`Commencez à taper pour rechercher un ${orderType === 'customers' ? 'client' : 'fournisseur'}`}
                    </p>
                  )
                ) : (
                  <div className="max-h-60 overflow-y-auto bg-white/5 rounded-xl">
                    {entities.length > 0 ? (
                      entities.map((entity) => (
                        <div
                          key={entity.id}
                          className={`flex items-center justify-between p-3 hover:bg-blue-500/20 cursor-pointer transition-colors border-b border-white/5 last:border-0 ${
                            (orderType === 'customers' && entity.id === selectedId) ||
                            (orderType === 'suppliers' && entity.id === selectedId)
                              ? 'bg-blue-500/30'
                              : ''
                          }`}
                          onClick={() => handleSelect(entity)}
                        >
                          <div>
                            <p className="text-white font-medium">{getEntityName(entity)}</p>
                            <p className="text-white/60 text-sm">
                              {entity.email && <span>{entity.email}</span>}
                              {entity.email && entity.phone && <span className="mx-1">•</span>}
                              {entity.phone && <span>{entity.phone}</span>}
                              {(entity.email || entity.phone) && entity.wilaya && <span className="mx-1">•</span>}
                              {entity.wilaya && <span>{entity.wilaya}</span>}
                            </p>
                          </div>
                          {((orderType === 'customers' && entity.id === selectedId) ||
                           (orderType === 'suppliers' && entity.id === selectedId)) ? (
                            <CheckIcon className="h-5 w-5 text-green-400" />
                          ) : (
                            <CheckIcon className="h-5 w-5 text-blue-500/0 group-hover:text-blue-500/50" />
                          )}
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center">
                        <p className="text-white/60 mb-2">
                          Aucun {orderType === 'customers' ? 'client' : 'fournisseur'} disponible
                        </p>
                        <button
                          type="button"
                          onClick={() => navigate(`/${orderType === 'customers' ? 'customers' : 'suppliers'}/new`)}
                          className="text-sm px-3 py-1.5 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                        >
                          Créer {orderType === 'customers' ? 'un client' : 'un fournisseur'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 pt-3 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Annuler
                </button>
                
                {selectedName && (
                  <button
                    type="button"
                    onClick={() => {
                      if (onSelect) {
                        onSelect(null);
                      }
                      setIsOpen(false);
                    }}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors"
                  >
                    Désélectionner
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomerDropdown;